<template>
  <b-row>
    <b-col cols="12" xl="12">
      <transition name="slide">
        <b-card>
          <template #header>
            <h5 class="m-0">Denúncias</h5>
          </template>
          <b-card-body>
            <filters-bar
              :filter.sync="filter"
              :complaint-status="ComplaintStatus"
              :profile="profile"
              :has-advanced-search="hasAdvancedSearch"
              :company-options="companyOptions"
              @apply="applyFilter"
            />

            <div v-if="!loading" class="mt-3 mb-2">
              <span class="text-muted">
                <strong>{{ data.total }}</strong> denúncias encontradas
              </span>
            </div>

            <div class="table-container">
              <complaints-table
                :items="data.docs"
                :headers="headers"
                :loading="loading"
                :sort-by="sortBy"
                :sort-desc="sortDesc"
                :search-term="filter.text"
                @row-clicked="showComplaintSummary"
                @sort-changed="onSortChanged"
              />
            </div>

            <nav class="pagination-nav">
              <b-pagination
                v-model="data.page"
                :total-rows="data.total"
                :per-page="data.limit"
                prev-text="Anterior"
                next-text="Próximo"
                align="center"
              ></b-pagination>
            </nav>
          </b-card-body>
        </b-card>
      </transition>
    </b-col>

    <summary-sidebar
      :visible.sync="isSummaryVisible"
      :complaint="selectedComplaint"
      :events="selectedComplaintEvents"
      @close="hideSummary"
    />
  </b-row>
</template>

<script>
import { mapGetters } from "vuex";
import { getComplaints, getComplaint, getEvents } from "@/api/complaint";
import { getCompanies } from "@/api/company";
import FiltersBar from "./FiltersBar.vue";
import ComplaintsTable from "./ComplaintsTable.vue";
import SummarySidebar from "./SummarySidebar.vue";

// Status constants (numéricos como esperado pela API)
const ComplaintStatus = {
  NEW: 0,
  PROCESSING: 1,
  CONCLUDED: 2,
  // Aliases para compatibilidade
  OPEN: 0,
  IN_PROGRESS: 1,
  RESOLVED: 2,
  CLOSED: 3,
};

export default {
  name: "ComplaintsList",
  components: { FiltersBar, ComplaintsTable, SummarySidebar },
  data() {
    return {
      sortBy: "createdAt",
      sortDesc: true,
      filter: {
        text: "",
        status: null,
        onlyMyCommittee: false,
        companyId: null,
        startDate: null,
        endDate: null,
      },
      data: {
        docs: [],
        total: 0,
        limit: 15,
        page: 1,
        pages: 1,
      },
      loading: false,
      ComplaintStatus,
      companies: [],
      selectedComplaint: null,
      selectedComplaintEvents: [],
      isSummaryVisible: false,
    };
  },
  computed: {
    ...mapGetters(["profile", "permissions"]),
    isRootUser() {
      return this.profile.role === "root";
    },
    companyOptions() {
      const options = [{ value: null, text: "Todas as Empresas" }];
      this.companies.forEach((company) => {
        options.push({
          value: company._id,
          text: company.fantasyName || company.name,
        });
      });
      return options;
    },
    hasAdvancedSearch() {
      return true;
    },
    headers() {
      const baseHeaders = [
        { key: "companyId", label: "Nº Seq.", sortable: true },
        { key: "company", label: "Empresa", sortable: true },
        { key: "protocol", label: "Protocolo", sortable: true },
        { key: "createdAt", label: "Criada em", sortable: true },
        { key: "updatedAt", label: "Atualizada em", sortable: true },
        { key: "nature", label: "Natureza", sortable: true },
      ];

      if (this.filter.text && this.filter.text.trim()) {
        baseHeaders.push({
          key: "snippet",
          label: "Trecho do Relato",
          sortable: false,
        });
      }

      baseHeaders.push({ key: "status", label: "Status", sortable: true });

      return baseHeaders;
    },
    canEdit() {
      if (parseInt(this.profile.role) === this.profile.role) {
        return (
          (this.permissions.complaint && this.permissions.complaint.edit) ||
          this.permissions.complaint.create
        );
      }
      return true;
    },
  },
  watch: {
    "data.page": "fetchComplaints",
  },
  async mounted() {
    // Restaurar filtros salvos se existirem no sessionStorage
    const savedFilters = sessionStorage.getItem("complaints_list_filters");
    if (savedFilters) {
      try {
        const parsed = JSON.parse(savedFilters);
        if (parsed.filter) this.filter = parsed.filter;
        if (parsed.sortBy) this.sortBy = parsed.sortBy;
        if (parsed.sortDesc !== undefined) this.sortDesc = parsed.sortDesc;
        if (parsed.page) this.data.page = parsed.page;
      } catch (e) {
        console.error("Erro ao restaurar filtros:", e);
      }
    }

    if (this.isRootUser) {
      await this.fetchCompanies();
    }
    this.fetchComplaints();
  },
  methods: {
    async fetchCompanies() {
      try {
        const response = await getCompanies({ size: 1000, page: 1 });
        if (response && response.data && response.data.docs) {
          this.companies = response.data.docs;
        }
      } catch (error) {
        console.error("Erro ao carregar empresas:", error);
      }
    },
    applyFilter() {
      this.data.page = 1;
      this.fetchComplaints();
    },
    onSortChanged(sortOptions) {
      if (sortOptions.sortBy) {
        this.sortBy = sortOptions.sortBy;
        this.sortDesc = sortOptions.sortDesc || false;
      } else {
        this.sortBy = null;
        this.sortDesc = false;
      }
      this.fetchComplaints();
    },
    async showComplaintSummary(complaint) {
      try {
        this.selectedComplaintEvents = [];
        this.isSummaryVisible = true;

        const complaintResponse = await getComplaint(complaint._id);
        if (complaintResponse && complaintResponse.data) {
          this.selectedComplaint = complaintResponse.data;
        } else {
          this.selectedComplaint = complaint;
        }

        const eventsResponse = await getEvents(complaint._id);
        if (eventsResponse && eventsResponse.data) {
          this.selectedComplaintEvents = Array.isArray(eventsResponse.data)
            ? eventsResponse.data
            : eventsResponse.data.events || [];
        }
      } catch (error) {
        console.error("Erro ao carregar dados da denúncia:", error);
        this.selectedComplaint = complaint;
        this.selectedComplaintEvents = [];
        this.$bvToast.toast(
          "Não foi possível carregar todos os dados da denúncia",
          {
            title: "Aviso",
            variant: "warning",
            solid: true,
          }
        );
      }
    },
    hideSummary() {
      this.isSummaryVisible = false;
      this.selectedComplaint = null;
      this.selectedComplaintEvents = [];
    },
    async fetchComplaints() {
      // Salva o estado dos filtros no sessionStorage
      try {
        sessionStorage.setItem(
          "complaints_list_filters",
          JSON.stringify({
            filter: this.filter,
            sortBy: this.sortBy,
            sortDesc: this.sortDesc,
            page: this.data.page,
          })
        );
      } catch (e) {
        console.error("Erro ao salvar filtros no sessionStorage:", e);
      }

      this.loading = true;
      try {
        const query = {
          page: this.data.page,
          size: this.data.limit,
          sortBy: this.sortBy,
          sortDesc: this.sortDesc,
          filter: {
            text:
              this.filter.text && this.filter.text.trim()
                ? this.filter.text.trim()
                : undefined,
            status:
              this.filter.status !== null &&
              this.filter.status !== undefined &&
              this.filter.status !== ""
                ? this.filter.status
                : undefined,
            onlyMyCommittee:
              this.profile &&
              (this.profile.role === "company-root" ||
                this.profile.role === "root") &&
              this.filter.onlyMyCommittee === true
                ? true
                : undefined,
            companyId:
              this.filter.companyId !== null &&
              this.filter.companyId !== undefined
                ? this.filter.companyId
                : undefined,
            startDate:
              this.filter.startDate && this.filter.startDate.trim()
                ? this.filter.startDate.trim()
                : undefined,
            endDate:
              this.filter.endDate && this.filter.endDate.trim()
                ? this.filter.endDate.trim()
                : undefined,
          },
        };

        const response = await getComplaints(query);

        if (response && response.data) {
          if (Array.isArray(response.data)) {
            this.data.docs = response.data;
            this.data.total = response.total || response.data.length;
          } else if (response.data.docs) {
            this.data.docs = response.data.docs;
            this.data.total = response.data.total || 0;
            this.data.pages = response.data.pages || 1;
          } else if (response.data.data && Array.isArray(response.data.data)) {
            this.data.docs = response.data.data;
            this.data.total =
              response.data.totalCount ||
              response.data.total ||
              response.data.data.length;
            this.data.pages = response.data.totalPages || 1;
          } else {
            this.data.docs = [];
            this.data.total = 0;
          }
        } else {
          this.data.docs = [];
          this.data.total = 0;
        }
      } catch (error) {
        console.error("Erro ao carregar denúncias:", error);
        let errorMessage = "Erro ao carregar denúncias";
        if (
          error.response &&
          error.response.data &&
          error.response.data.message
        ) {
          errorMessage = error.response.data.message;
        } else if (error.message) {
          errorMessage = error.message;
        }
        this.$bvToast.toast(errorMessage, {
          title: "Erro",
          variant: "danger",
          solid: true,
        });
        this.data.docs = [];
        this.data.total = 0;
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.table-container {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  border: 1px solid #e9ecef;
  overflow: hidden;
}

.pagination-nav {
  margin-top: 1.5rem;
}

::v-deep .page-item .page-link {
  border-radius: 6px;
  margin: 0 3px;
  border: 1px solid #e9ecef;
  color: #495057;
  transition: background-color 0.2s, color 0.2s;
}

::v-deep .page-item .page-link:hover {
  background-color: #f0f2f5;
  color: #0056b3;
}

::v-deep .page-item.active .page-link {
  background-color: #007bff;
  border-color: #007bff;
  color: #fff;
}

::v-deep .page-item.disabled .page-link {
  background-color: transparent;
  color: #adb5bd;
}
</style>
